import type { FilterChipType } from "marketplace/_views/filters/filter-chips";
import {
  EntityTypeInclusionFilterName,
  isEntityTypeInclusionFilterName,
} from "marketplace/search/entity-type-inclusion-filters";

/**
 * The marketplace filters, grouped by what they ask about, and marked with the one kind of profile
 * that has the field. Before, all 25 were one list in declaration order: nothing said that asking
 * who is on call leaves only people in the results, or that a firm is the only thing with a
 * headcount, and the sidebar read as a wall of identical rows.
 *
 * Which kind of profile a filter narrows is the API's, not a choice made here:
 * `MarketplaceEFSearchService` applies City and State to people and firms, the headcount and the
 * founding year to firms, and everything else to people. It applies none of them to teams, and a
 * search narrowed by a filter a kind of profile does not have leaves that kind out.
 */
export type FilterSection = {
  id: string;
  title: string;
  /** The kind of profile the section's filters narrow to, when it is only one. */
  appliesTo?: { filterName: EntityTypeInclusionFilterName; label: string };
  filterNames: readonly string[];
  /** The profile types are always in view; every other section folds. */
  collapsible: boolean;
  /** Open on first render even when nothing in it is set. */
  openByDefault: boolean;
};

const INDIVIDUALS = { filterName: "includeIndividuals", label: "Individuals" } as const;
const COMPANIES = { filterName: "includeCompanies", label: "Companies" } as const;

export const FILTER_SECTIONS: readonly FilterSection[] = [
  {
    id: "show",
    title: "Show",
    filterNames: ["includeCompanies", "includeTeams", "includeIndividuals"],
    collapsible: false,
    openByDefault: true,
  },
  {
    id: "location",
    title: "Location",
    filterNames: ["city", "state"],
    collapsible: true,
    openByDefault: true,
  },
  {
    id: "credentials",
    title: "Credentials",
    appliesTo: INDIVIDUALS,
    filterNames: ["regionsLicensed", "regionsRegistered", "professionalRegistration", "school", "degree"],
    collapsible: true,
    openByDefault: false,
  },
  {
    id: "expertise",
    title: "Expertise",
    appliesTo: INDIVIDUALS,
    filterNames: ["practiceAreas", "technicalBackgrounds", "technicalLanguages", "fluentLanguages"],
    collapsible: true,
    openByDefault: false,
  },
  {
    id: "experience",
    title: "Experience",
    appliesTo: INDIVIDUALS,
    filterNames: ["yearsExperience", "applicationsDrafted", "allowedApplications"],
    collapsible: true,
    openByDefault: false,
  },
  {
    id: "rates",
    title: "Rates and coverage",
    appliesTo: INDIVIDUALS,
    filterNames: ["billingRate", "billingUnit", "insuredAmount"],
    collapsible: true,
    openByDefault: false,
  },
  {
    id: "availability",
    title: "Availability",
    appliesTo: INDIVIDUALS,
    filterNames: ["isOnCall", "collaborates", "potentialDiversityCandidate"],
    collapsible: true,
    openByDefault: false,
  },
  {
    id: "companies",
    title: "Firm size and age",
    appliesTo: COMPANIES,
    filterNames: ["numberOfEmployees", "yearEstablished"],
    collapsible: true,
    openByDefault: false,
  },
];

/** Where a filter no section names goes, so a new one is never silently left out of the sidebar. */
const MORE: FilterSection = {
  id: "more",
  title: "More",
  filterNames: [],
  collapsible: true,
  openByDefault: false,
};

export type FilterSectionGroup = { section: FilterSection; filters: FilterChipType[] };

/**
 * The filters to draw, section by section, in each section's own order. Hidden filters and sections
 * with nothing in them are left out: the vendor-team pickers have no profile-type filters, so they
 * have no Show section.
 */
export function groupFiltersIntoSections(chips: readonly FilterChipType[]): FilterSectionGroup[] {
  const visible = chips.filter((chip) => !chip.hidden);
  const placed = new Set<string>();
  const groups: FilterSectionGroup[] = [];

  for (const section of FILTER_SECTIONS) {
    const filters = section.filterNames
      .map((filterName) => visible.find((chip) => chip.filterName === filterName))
      .filter((chip): chip is FilterChipType => chip !== undefined);
    for (const filter of filters) placed.add(filter.filterName);
    if (filters.length > 0) groups.push({ section, filters });
  }

  const unplaced = visible.filter((chip) => !placed.has(chip.filterName));
  if (unplaced.length > 0) groups.push({ section: MORE, filters: unplaced });
  return groups;
}

/** How many of a section's filters are narrowing the results. A profile type is on by default. */
export function countActiveFilters(filters: readonly FilterChipType[]): number {
  return filters.filter((filter) =>
    isEntityTypeInclusionFilterName(filter.filterName) ? !filter.enabled : filter.enabled,
  ).length;
}
